import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash } from "@phosphor-icons/react";
import { api, errorMessage, fmtDate, inr } from "@/lib/api";
import { AdminCard, adminBtn, adminInput } from "./AdminLayout";

const EMPTY = { code: "", type: "percent", value: 10, min_order: 0, max_uses: "", expires_at: "", active: true };

export default function AdminCoupons() {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const { data: coupons = [] } = useQuery({ queryKey: ["admin-coupons"], queryFn: async () => (await api.get("/admin/coupons")).data });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-coupons"] });

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", { ...form, value: Number(form.value), min_order: Number(form.min_order), max_uses: form.max_uses ? Number(form.max_uses) : null, expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59").toISOString() : null });
      toast.success("Coupon created"); setForm(EMPTY); refresh();
    } catch (err) { toast.error(errorMessage(err)); }
  };
  const toggle = async (c) => { await api.put(`/admin/coupons/${c.coupon_id}`, { ...c, active: !c.active }); refresh(); };
  const del = async (c) => { if (window.confirm(`Delete ${c.code}?`)) { await api.delete(`/admin/coupons/${c.coupon_id}`); refresh(); } };

  return (
    <div className="space-y-6" data-testid="admin-coupons-page">
      <h1 className="text-2xl font-medium tracking-tight">Coupons</h1>
      <div className="grid gap-6 lg:grid-cols-3">
        <AdminCard title="New coupon">
          <form onSubmit={create} className="space-y-3 text-sm">
            <input required placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={`${adminInput} uppercase`} data-testid="coupon-code-input" />
            <div className="grid grid-cols-2 gap-2">
              <select value={form.type} onChange={set("type")} className={adminInput} data-testid="coupon-type-select"><option value="percent">% off</option><option value="flat">₹ off</option></select>
              <input required type="number" min={1} value={form.value} onChange={set("value")} className={adminInput} data-testid="coupon-value-input" />
            </div>
            <label className="block"><span className="mb-1 block text-xs text-muted-foreground">Minimum order (₹)</span><input type="number" min={0} value={form.min_order} onChange={set("min_order")} className={adminInput} data-testid="coupon-min-input" /></label>
            <label className="block"><span className="mb-1 block text-xs text-muted-foreground">Max uses (blank = unlimited)</span><input type="number" min={1} value={form.max_uses} onChange={set("max_uses")} className={adminInput} data-testid="coupon-max-uses-input" /></label>
            <label className="block"><span className="mb-1 block text-xs text-muted-foreground">Expires on</span><input type="date" value={form.expires_at} onChange={set("expires_at")} className={adminInput} data-testid="coupon-expires-input" /></label>
            <button type="submit" className={`${adminBtn} w-full justify-center`} data-testid="coupon-create-button">Create coupon</button>
          </form>
        </AdminCard>
        <div className="overflow-x-auto rounded-lg border border-border bg-white lg:col-span-2">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Discount</th><th className="px-4 py-3">Min</th><th className="px-4 py-3">Used</th><th className="px-4 py-3">Expires</th><th className="px-4 py-3">Active</th><th /></tr></thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.coupon_id} className="border-t border-border/60" data-testid={`coupon-row-${c.code}`}>
                  <td className="px-4 py-3 font-mono font-semibold">{c.code}</td>
                  <td className="px-4 py-3">{c.type === "percent" ? `${c.value}%` : inr(c.value)}</td>
                  <td className="px-4 py-3">{c.min_order ? inr(c.min_order) : "—"}</td>
                  <td className="px-4 py-3">{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                  <td className="px-4 py-3 text-xs">{c.expires_at ? fmtDate(c.expires_at) : "Never"}</td>
                  <td className="px-4 py-3"><button onClick={() => toggle(c)} className={`rounded-full px-2 py-0.5 text-xs font-semibold ${c.active ? "bg-[#EEF1E4] text-accent" : "bg-muted text-muted-foreground"}`} data-testid={`coupon-toggle-${c.code}`}>{c.active ? "Active" : "Paused"}</button></td>
                  <td className="px-4 py-3 text-right"><button onClick={() => del(c)} className="text-muted-foreground hover:text-destructive" data-testid={`coupon-delete-${c.code}`}><Trash /></button></td>
                </tr>
              ))}
              {coupons.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No coupons yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
